import { Layers } from 'lucide-react'
import type { CalendarEvent } from '@trackmind/core'
import { CalendarEventRow } from '@/components/CalendarEventRow'

/**
 * One slot of the day's calendar, which may hold more than one meeting.
 *
 * When two connected calendars are booked over each other the app shows
 * BOTH — the way a calendar app does — rather than picking a winner and
 * hiding the loser. Hiding is the dangerous option: the meeting you never
 * see is the one you miss, and the app has no way of knowing which of the
 * two actually matters today.
 *
 * What the user's calendar preference (Settings → Calendar) does control is
 * the ORDER: the preferred calendar comes first and the rest recede. Until
 * they set one, the calendars rank equally and nothing is demoted on the
 * strength of a default — hence `hasPreference`.
 *
 * Two-view: side by side on desktop, where there's width to compare them at a
 * glance (the closest thing a list can do to Google Calendar's columns);
 * stacked inside the same marked container on phones, because two 44px-tall
 * rows in 390px would leave nothing but ellipses.
 */
export function CalendarEventGroup({
  events,
  colorFor,
  nameFor,
  hasPreference = false,
  compact = false,
}: {
  events: CalendarEvent[]
  colorFor: (sourceId: string) => string | null
  nameFor?: (sourceId: string) => string | null
  hasPreference?: boolean
  compact?: boolean
}) {
  if (events.length === 0) return null

  if (events.length === 1) {
    return (
      <CalendarEventRow
        event={events[0]}
        color={colorFor(events[0].source_id)}
        compact={compact}
      />
    )
  }

  const preferredName = hasPreference ? nameFor?.(events[0].source_id) : null

  return (
    <div className="rounded-lg border border-dashed border-amber-500/40 bg-amber-500/5 p-1.5 space-y-1.5">
      <div className="flex flex-wrap items-center gap-x-1.5 px-1 text-[11px] font-medium text-amber-600 dark:text-amber-400">
        <Layers className="h-3 w-3 shrink-0" />
        <span>{events.length} at the same time</span>
        {preferredName && (
          <span className="text-muted-foreground font-normal">· {preferredName} first</span>
        )}
      </div>
      <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
        {events.map((event, index) => (
          <CalendarEventRow
            key={event.id}
            event={event}
            color={colorFor(event.source_id)}
            compact
            // Only recede behind an explicit preference. Dimming a real
            // meeting because of a default would be the app inventing an
            // opinion it doesn't have.
            subdued={hasPreference && index > 0}
          />
        ))}
      </div>
    </div>
  )
}
